import classNames from 'classnames'
import { useMemo, useState, useEffect, useCallback } from 'react'
import { useInView } from 'react-intersection-observer'

import type { Options, SeriesFlagsOptions } from 'highcharts'
import type { Chart as ChartApi } from 'highcharts/highstock'
import type Highcharts from 'highcharts/highstock'
import type { ReactElement, ReactNode, MouseEvent } from 'react'
import type { IAnalyticsApiFn, IAnalyticsParams, IAnalyticTooltip } from '~/types/analytic'
import type { ICustomChartOptions } from '~/types/chart'
import type { IAppEvent, IFormattedEvent } from '~/types/event'
import type { ISeries } from '~/types/series'

import AnalyticBlockMenu from '~/components/AnalyticBlocksMenu'
import AnalyticGenericBlock, { AnalyticBlockFilter } from '~/components/AnalyticGenericBlock'
import AnalyticTooltip from '~/components/AnalyticsTooltip'
import AnalyticWarningTooltip from '~/components/AnalyticsWarningTooltip'
import Chart from '~/components/Chart'
import DropdownMenu from '~/components/DropdownMenu'
import { dataNotAvailableText } from '~/const'
import { useAnalyticsDataQuery } from '~/queries/analytics'
import { APP_EVENTS } from '~/types/event'
import { isEmptyData } from '~/utils/chart'

import './styles.scss'

export interface Props {
  analyticsApiFn?: IAnalyticsApiFn
  analyticsParams: IAnalyticsParams
  analyticsTooltipValue?: IAnalyticTooltip
  appIds: string[]
  className?: string
  constructorType?: string
  defaultRange?: number
  displayWarning?: boolean
  doFetch?: boolean
  eventsData?: IAppEvent[]
  filters?: ReactNode
  frontendDataKey?: string
  hasFavourite?: boolean
  isLoading?: boolean
  lazy?: boolean
  message?: ReactNode
  onGetHighchartsRef?: (highchartsRef: typeof Highcharts) => void
  onSeriesLoading?: (isLoading: boolean) => void
  options?: Options & ICustomChartOptions
  overallDecimals?: number
  overallSortPredicate?: (a: ISeries, b: ISeries) => number
  processOverallPercentage?: boolean
  series?: { data: ISeries[] | number[][] | undefined }
  title: ReactNode
  warningMessage?: string
  withOverall?: boolean
  xAxisOptions?: Options
}

const GraphBlock = ({
  analyticsApiFn,
  analyticsParams,
  analyticsTooltipValue,
  appIds,
  className,
  constructorType,
  defaultRange,
  displayWarning,
  doFetch = true,
  eventsData,
  filters,
  frontendDataKey,
  isLoading,
  lazy = true,
  message,
  onGetHighchartsRef,
  onSeriesLoading,
  options,
  series,
  title,
  warningMessage,
}: Props): ReactElement => {
  /** Local state */

  const { inView, ref } = useInView({ triggerOnce: true })

  const [chartApi, setChartApi] = useState<ChartApi>()

  const exportMenu = [
    {
      items: [
        { label: 'Download CSV', onClick: () => chartApi?.downloadCSV() },
        { label: 'Download XLS', onClick: () => chartApi?.downloadXLS() },
      ],
      sectionTitle: 'export',
    },
  ]

  /** Queries & Mutations */

  const {
    data,
    isError: isApiError,
    isLoading: isSeriesLoading,
  } = useAnalyticsDataQuery(
    analyticsParams,
    appIds,
    {
      doFetch: lazy ? doFetch && inView : doFetch,
      series,
    },
    analyticsApiFn
  )
  const firstDataDate = data?.data?.[0]?.data?.[0]?.[0] || 0

  const appEventsData: IFormattedEvent[] = useMemo(
    () =>
      eventsData
        ?.filter(({ date }) => {
          const eventDateTimestamp = new Date(date).getTime()
          return eventDateTimestamp >= firstDataDate
        })
        .map(({ date, event_type, value }) => {
          const { backgroundColor, fontColor } = APP_EVENTS[event_type] || {}

          return {
            color: '#1d1d1d',
            eventType: event_type,
            fillColor: backgroundColor,
            style: {
              color: fontColor,
            },
            text: value,
            title: value,
            x: new Date(date).getTime(),
          }
        }) || [],
    [eventsData, firstDataDate]
  )

  const appEventsOnAxis: SeriesFlagsOptions | undefined = useMemo(
    () =>
      appEventsData.length > 0
        ? {
            data: appEventsData.sort((a, b) => {
              if (a.x > b.x) return 1
              if (a.x < b.x) return -1
              return 0
            }),
            includeInDataExport: false,
            name: 'Events',
            shape: 'squarepin',
            showInLegend: false,
            style: {
              fontFamily: 'ModernGothic',
              fontSize: '11px',
              fontWeight: '400',
            },
            tooltip: {
              footerFormat: '</div>',
              headerFormat: '<div><div>{point.key}</div>',
              pointFormatter: function () {
                // @ts-expect-error text does not exist on type Point
                if (this.text) {
                  // @ts-expect-error text does not exist on type Point
                  return `<div><b>${this.text}</b></div>`
                }
                return ''
              },
              xDateFormat: '%b %d, %Y',
            },
            type: 'flags',
          }
        : undefined,
    [appEventsData]
  )

  const _series = useMemo(() => series || data, [data, series])

  const chartOptions = useMemo((): Options & ICustomChartOptions => {
    const finalOptions = {
      ...options,
      defaultRange,
      exportFileName: analyticsParams.feature,
    }

    return finalOptions
  }, [analyticsParams.feature, defaultRange, options])

  /** Effects */

  useEffect(() => {
    onSeriesLoading && onSeriesLoading(isSeriesLoading)
  }, [isSeriesLoading, onSeriesLoading])

  /** Handlers */

  const handleGetChartApi = useCallback((chartApi: ChartApi) => setChartApi(chartApi), [])

  const handleGraphContainerClick = (evt: MouseEvent) => evt.stopPropagation()

  const renderChildren = useCallback((): ReactNode => {
    if (displayWarning && warningMessage) return <span>{warningMessage}</span>
    if (isApiError) return <span>API error</span>
    if (!_series || isEmptyData(_series)) return <span>{dataNotAvailableText}</span>

    return (
      <Chart
        constructorType={constructorType}
        eventSeries={appEventsOnAxis}
        getChartApi={handleGetChartApi}
        getHighchartsRef={onGetHighchartsRef}
        options={chartOptions}
        series={_series.data || []}
      />
    )
  }, [
    _series,
    appEventsOnAxis,
    chartOptions,
    constructorType,
    displayWarning,
    handleGetChartApi,
    isApiError,
    onGetHighchartsRef,
    warningMessage,
  ])

  return (
    <AnalyticGenericBlock
      className={classNames('analytics-generic-block-container__graph', className, {
        'has-filters': !!filters,
        'no-data': !_series || isEmptyData(_series),
      })}
      isLoading={isLoading || isSeriesLoading}
      title={title}
    >
      {/**
       * This id is used as an anchor.
       * The `useScrollToElement` in the generic `Layout` component will detect if an anchor is present
       * in the url and scroll to it if the anchor is present in the view.
       */}
      <div id={frontendDataKey} ref={ref} />
      <AnalyticBlockMenu>
        {message ? <AnalyticWarningTooltip message={message} /> : null}
        {analyticsTooltipValue ? <AnalyticTooltip value={analyticsTooltipValue} /> : null}
        <DropdownMenu menu={exportMenu} onMouseDown={(e: MouseEvent) => e.stopPropagation()} />
      </AnalyticBlockMenu>
      <AnalyticBlockFilter>{filters}</AnalyticBlockFilter>
      <div
        className={classNames('container graph-container', {
          'with-filters': !!filters,
        })}
        onMouseDown={handleGraphContainerClick}
      >
        {renderChildren()}
      </div>
    </AnalyticGenericBlock>
  )
}

export default GraphBlock
